import { useId } from "react"
import type { z } from "zod"
import { SettingRow } from "~/components/settings/SettingRow"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from "~/components/ui/select"
import { formDiscordSettingsSchema } from "~/lib/schemas"

export function FormDiscordFields({
	value,
	onChange,
	perForm = false,
	teamDefaultLabel = "Off",
	guilds,
	channels,
	loadingGuilds = false,
	loadingChannels = false,
	disabled = false
}: {
	value: z.infer<typeof formDiscordSettingsSchema>
	onChange: (value: z.infer<typeof formDiscordSettingsSchema>) => void
	perForm?: boolean
	teamDefaultLabel?: string
	guilds: { id: string; name: string }[]
	channels: { id: string; name: string }[]
	loadingGuilds?: boolean
	loadingChannels?: boolean
	disabled?: boolean
}) {
	const id = useId()
	return (
		<div className="flex flex-col gap-4">
			<SettingRow label="Responses" htmlFor={`${id}-mode`}>
				<Select
					value={value.mode}
					onValueChange={(mode) =>
						onChange({
							...value,
							mode: formDiscordSettingsSchema.shape.mode.parse(mode)
						})
					}
					disabled={disabled}
				>
					<SelectTrigger id={`${id}-mode`}>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{perForm && (
							<SelectItem value="inherit">
								Team default ({teamDefaultLabel})
							</SelectItem>
						)}
						<SelectItem value="off">Off</SelectItem>
						<SelectItem value="channel">Send to a Discord channel</SelectItem>
					</SelectContent>
				</Select>
			</SettingRow>
			{value.mode === "channel" && (
				<>
					<SettingRow label="Server" htmlFor={`${id}-guild`}>
						<Select
							value={value.guildId}
							onValueChange={(guildId) =>
								onChange({ ...value, guildId, channelId: "" })
							}
							disabled={disabled || loadingGuilds}
						>
							<SelectTrigger id={`${id}-guild`}>
								<SelectValue
									placeholder={
										loadingGuilds ? "Loading servers…" : "Select a server"
									}
								/>
							</SelectTrigger>
							<SelectContent>
								{guilds.map((guild) => (
									<SelectItem key={guild.id} value={guild.id}>
										{guild.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</SettingRow>
					<SettingRow label="Channel" htmlFor={`${id}-channel`}>
						<Select
							value={value.channelId}
							onValueChange={(channelId) => onChange({ ...value, channelId })}
							disabled={disabled || !value.guildId || loadingChannels}
						>
							<SelectTrigger id={`${id}-channel`}>
								<SelectValue
									placeholder={
										loadingChannels ? "Loading channels…" : "Select a channel"
									}
								/>
							</SelectTrigger>
							<SelectContent>
								{channels.map((channel) => (
									<SelectItem key={channel.id} value={channel.id}>
										#{channel.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</SettingRow>
				</>
			)}
		</div>
	)
}
